export type PlatformIdentityStatus =
  | "checking"
  | "administrator"
  | "authenticated"
  | "signed-out"
  | "refresh-failed"
  | "identity-failed"
  | "network-error";

export interface PlatformIdentity {
  readonly authenticated: boolean;
  readonly administrator: boolean;
  readonly displayName: string | null;
  readonly source: string | null;
  readonly status: PlatformIdentityStatus;
  readonly detail: string;
  readonly roles: readonly string[];
  readonly httpStatus: number | null;
}

let cachedIdentity: Promise<PlatformIdentity> | null = null;
let cachedAccessToken: string | null = null;

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "https://api.littlecastle.online";
const REFRESH_PATH =
  (import.meta.env.VITE_AUTH_REFRESH_PATH as string | undefined)?.trim() ?? "/api/v1/auth/refresh";
const IDENTITY_PATH =
  (import.meta.env.VITE_AUTH_ME_PATH as string | undefined)?.trim() ?? "/api/v1/auth/me";

function endpoint(path: string): string {
  return path.startsWith("http") ? path : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function read(record: Record<string, unknown> | null, key: string): unknown {
  return record && Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

function flattenStrings(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenStrings);
  const record = asRecord(value);
  if (!record) return [];
  return ["name", "displayName", "slug", "role", "group", "code", "value"].flatMap((key) =>
    flattenStrings(read(record, key)),
  );
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replaceAll("_", "-").replaceAll(" ", "-");
}

function parseIdentity(payload: unknown, source: string): PlatformIdentity {
  const root = asRecord(payload);
  const data = asRecord(read(root, "data"));
  const user =
    asRecord(read(root, "user")) ??
    asRecord(read(data, "user")) ??
    asRecord(read(root, "account")) ??
    asRecord(read(data, "account")) ??
    root;
  const roles = [
    read(user, "roles"),
    read(user, "groups"),
    read(user, "permissions"),
    read(root, "roles"),
    read(root, "groups"),
    read(data, "roles"),
    read(data, "groups"),
  ]
    .flatMap(flattenStrings)
    .map(normalize)
    .filter((value, index, values) => values.indexOf(value) === index);
  const administrator =
    read(user, "isAdmin") === true ||
    read(user, "isAdministrator") === true ||
    read(user, "administrator") === true ||
    roles.some((role) =>
      [
        "administrator",
        "administrators",
        "admin",
        "platform-administrator",
        "platform-admin",
        "littlecastle-administrator",
        "littlecastle-admin",
      ].includes(role),
    );
  const id = read(user, "id") ?? read(user, "userId") ?? read(user, "email");
  const displayName = read(user, "displayName") ?? read(user, "name") ?? read(user, "email");
  const authenticated = id !== undefined || read(root, "authenticated") === true;
  return {
    authenticated,
    administrator,
    displayName: typeof displayName === "string" ? displayName : null,
    source,
    status: administrator ? "administrator" : authenticated ? "authenticated" : "signed-out",
    detail: administrator
      ? "Administrator role verified."
      : authenticated
        ? `Signed in, but no administrator role was found${roles.length ? ` (${roles.join(", ")})` : "."}`
        : "No signed-in platform identity was returned.",
    roles,
    httpStatus: 200,
  };
}

function failure(
  status: PlatformIdentityStatus,
  detail: string,
  source: string | null,
  httpStatus: number | null,
): PlatformIdentity {
  return {
    authenticated: false,
    administrator: false,
    displayName: null,
    source,
    status,
    detail,
    roles: [],
    httpStatus,
  };
}

async function resolveIdentity(): Promise<PlatformIdentity> {
  const refreshUrl = endpoint(REFRESH_PATH);
  const identityUrl = endpoint(IDENTITY_PATH);
  try {
    const refresh = await fetch(refreshUrl, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!refresh.ok) {
      const result = failure(
        refresh.status === 401 ? "signed-out" : "refresh-failed",
        `Session refresh failed with HTTP ${refresh.status}.`,
        refreshUrl,
        refresh.status,
      );
      console.warn("[Defense admin access] refresh failed", result);
      return result;
    }
    const refreshPayload = asRecord((await refresh.json()) as unknown);
    const accessToken =
      read(refreshPayload, "accessToken") ??
      read(read(refreshPayload, "data") as Record<string, unknown> | null, "accessToken");
    if (typeof accessToken !== "string" || accessToken.length < 10) {
      cachedAccessToken = null;
      const result = failure(
        "refresh-failed",
        "Session refresh succeeded but did not return an access token.",
        refreshUrl,
        refresh.status,
      );
      console.warn("[Defense admin access] refresh token missing", result);
      return result;
    }
    cachedAccessToken = accessToken;
    const identity = await fetch(identityUrl, {
      credentials: "include",
      headers: { Accept: "application/json", Authorization: `Bearer ${accessToken}` },
    });
    if (!identity.ok) {
      const result = failure(
        identity.status === 401 ? "signed-out" : "identity-failed",
        `Identity request failed with HTTP ${identity.status}.`,
        identityUrl,
        identity.status,
      );
      console.warn("[Defense admin access] identity failed", result);
      return result;
    }
    const result = parseIdentity((await identity.json()) as unknown, identityUrl);
    console.info("[Defense admin access] identity resolved", {
      authenticated: result.authenticated,
      administrator: result.administrator,
      displayName: result.displayName,
      roles: result.roles,
      source: result.source,
    });
    return result;
  } catch (error) {
    cachedAccessToken = null;
    const result = failure(
      "network-error",
      error instanceof Error ? error.message : "Unable to reach the LittleCastle account service.",
      null,
      null,
    );
    console.warn("[Defense admin access] network error", result);
    return result;
  }
}

export function getPlatformIdentity(forceRefresh = false): Promise<PlatformIdentity> {
  if (forceRefresh) {
    cachedIdentity = null;
    cachedAccessToken = null;
  }
  cachedIdentity ??= resolveIdentity();
  return cachedIdentity;
}

export async function getPlatformAccessToken(forceRefresh = false): Promise<string | null> {
  const identity = await getPlatformIdentity(forceRefresh);
  return identity.authenticated ? cachedAccessToken : null;
}

export async function currentPlayerIsAdministrator(forceRefresh = false): Promise<boolean> {
  const identity = await getPlatformIdentity(forceRefresh);
  return identity.authenticated && identity.administrator;
}

export async function platformRequest(
  path: string,
  init: RequestInit = {},
  retryAuthentication = true,
): Promise<Response> {
  const identity = await getPlatformIdentity();
  if (!identity.authenticated || !cachedAccessToken)
    return new Response(null, { status: 401, statusText: "Not signed in" });
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Authorization", `Bearer ${cachedAccessToken}`);
  if (init.body !== undefined && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  const response = await fetch(endpoint(path), { ...init, credentials: "include", headers });
  if (response.status !== 401 || !retryAuthentication) return response;
  const refreshed = await getPlatformIdentity(true);
  if (!refreshed.authenticated || !cachedAccessToken) return response;
  return platformRequest(path, init, false);
}
